import * as React from 'react';
import type { Responsive, SpaceKey } from '../../../types';
import { isBreakpointMap, BREAKPOINT_ORDER } from '../../../utils/responsive';
import {
  root as rootCls,
  gap as gapCls,
  rowGap as rowGapCls,
  columnGap as columnGapCls,
  flow as flowCls,
} from './Grid.css';

export type GridColumns = number | string;

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Number of equal columns, or a custom `grid-template-columns` string.
   * Accepts a flat value or a responsive map.
   */
  columns?: Responsive<GridColumns>;
  /** Gap between cells. */
  gap?: SpaceKey;
  /** Row gap override (defaults to gap). */
  rowGap?: SpaceKey;
  /** Column gap override (defaults to gap). */
  columnGap?: SpaceKey;
  /** Use dense packing. */
  dense?: boolean;
  as?: React.ElementType;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

function toTemplate(c: GridColumns): string {
  if (typeof c === 'number') return `repeat(${c}, minmax(0, 1fr))`;
  return c;
}

/**
 * Build the CSS-variable map for the responsive `columns` prop. Each
 * breakpoint's variable holds a concrete `grid-template-columns` value;
 * the stylesheet selects the matching variable via min-width media queries.
 */
function buildColumnVars(columns: Responsive<GridColumns>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (isBreakpointMap(columns)) {
    BREAKPOINT_ORDER.forEach((bp) => {
      const v = columns[bp];
      if (v !== undefined) out[`--grid-cols-${bp}`] = toTemplate(v);
    });
  } else {
    out['--grid-cols-xs'] = toTemplate(columns);
  }
  return out;
}

/**
 * Grid — CSS-grid layout primitive. Pass `columns={n}` for `n` equal
 * columns (`repeat(n, minmax(0, 1fr))`) or a `grid-template-columns`
 * string for custom tracks. `columns` also accepts a responsive map
 * (`{ xs, sm, md, lg, xl }`) which renders the appropriate template
 * via CSS variables + media queries.
 *
 * Use `gap` (keyed to the spacing scale — `SpaceKey`: 0–8, 10, 11, each a
 * distinct `theme.space` token) for symmetric gutters, or `rowGap` /
 * `columnGap` for asymmetric gutters. `dense` enables
 * `grid-auto-flow: dense` to fill in earlier holes.
 */
export function Grid({
  columns = 1,
  gap = 0,
  rowGap,
  columnGap,
  dense = false,
  as: Component = 'div',
  children,
  ref,
  className,
  style,
  ...rest
}: GridProps) {
  const cls = [
    rootCls,
    gapCls({ gap }),
    rowGap !== undefined ? rowGapCls({ rowGap }) : '',
    columnGap !== undefined ? columnGapCls({ columnGap }) : '',
    flowCls({ dense }),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const colVars = buildColumnVars(columns);

  return (
    <Component ref={ref} className={cls} style={{ ...colVars, ...style }} {...rest}>
      {children}
    </Component>
  );
}
Grid.displayName = 'Grid';
